import { ulid, unknownErrorToError, type UFile } from '@w/utils'

export type PreparedMedia = { path: string; status: 'ready'; url: string }
  | { path: string; status: 'unavailable'; error: Error }

/** One preparation session belongs to one principal-bound media client. */
export function createMediaPreparation(options: {
  active(): void
  get(path: string): Promise<UFile | null>
  getUrl(path: string): string
  imageLoader(url: string): Promise<void>
}) {
  const pending = new Map<string, Promise<PreparedMedia>>()
  const decoded = new Set<string>()
  const writes = new Set<(path: string) => void>()
  // Paths whose last preparation was unavailable. Cached query results that hold them
  // (staleTime Infinity) are invalidated once the path recovers, from any group.
  const failed = new Set<string>()
  function notify(path: string) {
    for (const onWrite of writes) {
      onWrite(path)
    }
  }
  function prepareOne(path: string): Promise<PreparedMedia> {
    options.active()
    const existing = pending.get(path)
    if (existing) return existing
    const task = (async (): Promise<PreparedMedia> => {
      try {
        const file = await options.get(path)
        options.active()
        if (!file) return { path, status: 'unavailable', error: new Error('Referenced media is unavailable') }
        const url = options.getUrl(path)
        if (!url) throw new Error('Media display URL is unavailable')
        if (file.blob.type.startsWith('image/') && !decoded.has(url)) {
          await options.imageLoader(url)
          options.active()
          decoded.add(url)
        }
        return { path, status: 'ready', url }
      } catch (error) {
        options.active()
        return { path, status: 'unavailable', error: unknownErrorToError(error) }
      }
    })().finally(() => { if (pending.get(path) === task) pending.delete(path) })
      .then(result => {
        if (result.status === 'unavailable') failed.add(path)
        else if (failed.delete(path)) notify(path)
        return result
      })
    pending.set(path, task)
    return task
  }
  return {
    sessionId: ulid(),
    subscribeMediaWrites: (onWrite: (path: string) => void) => {
      writes.add(onWrite)
      return () => { writes.delete(onWrite) }
    },
    written: (path: string) => { failed.delete(path); notify(path) },
    prepare: (paths: readonly string[]) => Promise.all([...new Set(paths)].map(path => prepareOne(path))),
  }
}
